import type { BundleMessage } from './BundleMessage.js';

export interface BundleOutputFile {
  /**
   * The relative path to the output file (starting from `BundleResult#outputPath`),
   * with forward slashes.
   */
  outputPath: string;

  /**
   * The relative path to the entry file (starting from `BundleResult#inputPath`),
   * with forward slashes.
   */
  entryPoint?: string;

  /**
   * The list of export names coming from the output file.
   */
  exports?: string[];
}

/**
 * Result returned by a bundler plugin (`BundlerPlugin#bundle()`).
 * Excludes properties that can be filled in automatically.
 */
export interface BundlerPluginResult {
  /**
   * The absolute output path of the bundle.
   */
  outputPath?: string;

  /**
   * The output files produced by bundling.
   */
  outputFiles?: BundleOutputFile[];

  /**
   * Errors encountered during bundling.
   */
  errors?: BundleMessage[];

  /**
   * Warnings encountered during bundling.
   */
  warnings?: BundleMessage[];

  /**
   * The raw input options passed along into the bundler, for debugging purposes.
   * May be missing if something prevented executing the bundler.
   */
  rawInput?: Record<string, unknown>;

  /**
   * The raw output emitted from the bundler, for debugging purposes.
   * May be missing if something prevented the bundler from returning output.
   */
  rawOutput?: Record<string, unknown>;

  /**
   * If incremental bundling is specified, provide a way to rebuild the project.
   */
  rebuild?: () => Promise<BundlerPluginResult>;

  /**
   * If incremental bundling is specified, provide a way to shut down the incremental build job.
   */
  dispose?: () => void;
}

/**
 * Final bundle result as returned by `bundle()` in `@ms-cloudpack/bundler`.
 */
// rawInput and rawOutput are intentionally removed.
// rebuild is wrapped to return a full BundleResult.
export interface BundleResult extends Omit<BundlerPluginResult, 'rebuild' | 'rawInput' | 'rawOutput'> {
  /**
   * The name of the bundler used. Will be set unless there was an error with the options.
   */
  bundlerName?: string;

  /**
   * The absolute input path of the entries.
   */
  inputPath: string;

  /**
   * The entry file paths map used to create the bundle output. See `BundleOptions.entries`.
   */
  entries?: Record<string, string>;

  /**
   * If incremental bundling is specified, provide a way to rebuild the project.
   */
  rebuild?: () => Promise<BundleResult>;
}
